import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, MessageCircle, Minus, Plus } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { CTABanner } from "@/components/CTABanner";
import { FAQS } from "@/data/content";
import { waLink } from "@/lib/site";

const FaqItem = ({ item, index, open, onToggle }) => (
  <div className="border-b border-border" data-testid={`faq-item-${index}`}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      data-testid={`faq-question-${index}`}
      className="flex w-full items-center justify-between gap-6 py-6 text-left"
    >
      <span className={`font-serif text-lg font-semibold transition-colors duration-300 sm:text-xl ${open ? "text-gold-dark" : "text-charcoal"}`}>
        {item.q}
      </span>
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
          open ? "border-gold bg-gold text-white" : "border-border text-charcoal-soft"
        }`}
      >
        {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
      </span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <p className="max-w-3xl pb-7 text-sm leading-relaxed text-charcoal-soft sm:text-base" data-testid={`faq-answer-${index}`}>
            {item.a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <>
      <SEO
        title="FAQ | Home Defect Inspection Questions Answered — Lobang"
        description="What our inspection covers, how long it takes, one-trip vs three-trip, when to book, BSC joint inspections and how to book via WhatsApp +65 8490 0316."
      />
      <PageHero
        eyebrow="FAQ"
        title="Questions, answered honestly"
        copy="Everything homeowners ask us before booking — scope, timing, reports and how the three visits work."
      />

      <section className="px-5 py-16 sm:px-8 sm:py-24" data-testid="faq-section">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div>
              {FAQS.map((f, i) => (
                <FaqItem key={f.q} item={f} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-12 rounded-[1.75rem] bg-cream p-8 text-center" data-testid="faq-contact-section">
              <h2 className="font-serif text-2xl font-semibold text-charcoal">Still curious?</h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-charcoal-soft">
                Ask us anything on WhatsApp — scope, timing, your specific flat layout. We reply fast.
              </p>
              <div className="mt-6 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <a
                  href={waLink("Hi Lobang! I have a question about your home inspection service.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="faq-whatsapp-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-wa px-7 py-3.5 text-sm font-bold text-white shadow-wa transition-transform duration-300 hover:scale-[1.04]"
                >
                  <MessageCircle className="h-4 w-4" />
                  Ask on WhatsApp
                </a>
                <Link
                  to="/contact"
                  data-testid="faq-contact-link"
                  className="inline-flex items-center gap-2 text-sm font-bold text-gold-dark transition-colors hover:text-gold"
                >
                  Visit the contact page
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
